/*
  Canonical shipping-option model for the MIG checkout (Teamwork UAT-36).

  The Insites ecommerce add-on has no buyer-selectable shipping method, so
  MIG adds four options on /checkout/shipping. This module is the SINGLE
  source of truth for the option keys, buyer-facing names, summary labels,
  next-step copy, the $50 Sydney-delivery fee, and the freight metadata
  written onto the order. Every consumer (the shipping selector, the order
  summary, the confirmation page, the payment mirror) imports from here so
  labels/keys can never drift.

  IMPORTANT — keep the enum→fee/status mapping in sync with the two Liquid
  copies that the server uses for the actual charge + order record:
    - modules/ecommerce/public/views/partials/checkout/compute_order_summary.liquid
      (server pricing — adds $50 for sydney_delivery, in DOLLARS not cents)
    - modules/ecommerce/public/forms/checkout/checkout_payment.liquid
      (order record — freight_supplier_name / freight_method / order_shipping_status)

  Money is in DOLLARS to match compute_order_summary (which compares the
  total to 1.00 and lets the Stripe partial multiply by 100).
*/

export type ShippingOptionKey =
  | 'venue_pickup'
  | 'airport_pickup'
  | 'sydney_delivery'
  | 'pack_and_send'

export interface ShippingOption {
  key: ShippingOptionKey
  /** Buyer-facing name in the selector. */
  name: string
  /** Short helper line under the name in the selector. */
  detail: string
  /** Label shown on the order-summary shipping row + confirmation. */
  summaryLabel: string
  /** One sentence telling the buyer the manual follow-up that's coming
   *  (confirmation page + buyer email). */
  nextStep: string
  /** Whether this option collects a delivery address. */
  requiresAddress: boolean
  /** True for the two collection options (no address, For Collection). */
  isPickup: boolean
  /** Flat shipping charge in AUD dollars (once per order). */
  amount: number
  /** Platform Order fields persisted for admin + emails. */
  freight_supplier_name: string
  freight_method: string
  /** Fulfilment status — rendered in admin/order-history via
   *  `replace "_"," " | capitalize`, so "For collection" / "For delivery". */
  order_shipping_status: 'for_collection' | 'for_delivery'
}

export const SHIPPING_OPTIONS: Record<ShippingOptionKey, ShippingOption> = {
  venue_pickup: {
    key: 'venue_pickup',
    name: 'Pick up from the exhibition venue at the end of the show',
    detail: 'We will contact you with further details',
    summaryLabel: 'Pickup — Free',
    nextStep: 'Our team will email you shortly with collection details and timing.',
    requiresAddress: false,
    isPickup: true,
    amount: 0,
    freight_supplier_name: 'Pickup — Exhibition Venue',
    freight_method: 'Collection',
    order_shipping_status: 'for_collection',
  },
  airport_pickup: {
    key: 'airport_pickup',
    name: 'Pick up from a location near the Kingsford Smith Airport',
    detail: '',
    summaryLabel: 'Pickup — Free',
    nextStep: 'Our team will email you shortly with collection details and timing.',
    requiresAddress: false,
    isPickup: true,
    amount: 0,
    freight_supplier_name: 'Pickup — Kingsford Smith Airport',
    freight_method: 'Collection',
    order_shipping_status: 'for_collection',
  },
  sydney_delivery: {
    key: 'sydney_delivery',
    name: 'Migration Galleries delivery (within Sydney only)',
    detail:
      'This applies to locations in the SA4 "Sydney" region and incurs a flat rate of $50.',
    summaryLabel: 'Sydney delivery — $50.00',
    nextStep: 'Our team will email you shortly to arrange delivery.',
    requiresAddress: true,
    isPickup: false,
    amount: 50,
    freight_supplier_name: 'Migration Galleries',
    freight_method: 'Local delivery',
    order_shipping_status: 'for_delivery',
  },
  pack_and_send: {
    key: 'pack_and_send',
    name: 'Shipping to all other locations via Pack&Send',
    detail: '',
    summaryLabel: 'Pack & Send — arranged separately',
    nextStep:
      'Our team will email you shortly. Pack & Send will then confirm your delivery address and arrange shipping payment directly, separate to this order.',
    requiresAddress: true,
    isPickup: false,
    amount: 0,
    freight_supplier_name: 'Pack & Send',
    freight_method: 'Pack & Send',
    order_shipping_status: 'for_delivery',
  },
}

/** Window CustomEvent name the shipping step dispatches (detail = the
 *  chosen option key string) so the OrderSummary right-rail can update
 *  its shipping line immediately as the buyer clicks, before submit. */
export const SHIPPING_OPTION_EVENT = 'mig:shipping-option'

/** Order-summary row value for a given option (right-rail + confirmation).
 *  Sydney shows the dollar amount; pickups "Free"; Pack & Send defers. */
export function shippingRowValue(key: string | null | undefined): string {
  const opt = getShippingOption(key)
  if (!opt) return ''
  if (opt.key === 'pack_and_send') return 'Arranged separately'
  if (opt.amount > 0) {
    return `$${opt.amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return 'Free'
}

/** Stable order the options render in the selector. */
export const SHIPPING_OPTION_ORDER: ShippingOptionKey[] = [
  'venue_pickup',
  'airport_pickup',
  'sydney_delivery',
  'pack_and_send',
]

export function isShippingOptionKey(value: unknown): value is ShippingOptionKey {
  return typeof value === 'string' && value in SHIPPING_OPTIONS
}

export function getShippingOption(key: string | null | undefined): ShippingOption | null {
  if (isShippingOptionKey(key)) return SHIPPING_OPTIONS[key]
  return null
}

export function isPickupOption(key: string | null | undefined): boolean {
  return getShippingOption(key)?.isPickup ?? false
}

export function shippingFeeFor(key: string | null | undefined): number {
  return getShippingOption(key)?.amount ?? 0
}

/* ----------------------------------------------------------------------
   Greater Sydney GCCSA postcode gate (Option 3 — sydney_delivery).
   Authoritative list supplied by MG (UAT-36 spec §6). Includes Blue
   Mountains, Penrith, Camden, Campbelltown — so this is an explicit
   membership set, NOT a numeric range (codes like 2750/2780/2560/2570
   fall outside any 2000–2799 range).
   Mirror this list in compute_order_summary.liquid is NOT needed: the
   $50 is applied server-side only when the option is sydney_delivery,
   and the option can only be chosen here after passing this gate.
---------------------------------------------------------------------- */
export const GREATER_SYDNEY_POSTCODES: ReadonlySet<string> = new Set([
  '2000', '2006', '2007', '2008', '2009', '2010', '2011', '2015', '2016', '2017',
  '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027',
  '2028', '2029', '2030', '2031', '2032', '2033', '2034', '2035', '2036', '2037',
  '2038', '2039', '2040', '2041', '2042', '2043', '2044', '2045', '2046', '2047',
  '2048', '2049', '2050', '2052', '2060', '2061', '2062', '2063', '2064', '2065',
  '2066', '2067', '2068', '2069', '2070', '2071', '2072', '2073', '2074', '2075',
  '2076', '2077', '2079', '2080', '2081', '2082', '2083', '2084', '2085', '2086',
  '2087', '2088', '2089', '2090', '2092', '2093', '2094', '2095', '2096', '2097',
  '2099', '2100', '2101', '2102', '2103', '2104', '2105', '2106', '2107', '2108',
  '2109', '2110', '2111', '2112', '2113', '2114', '2115', '2116', '2117', '2118',
  '2119', '2120', '2121', '2122', '2123', '2125', '2126', '2127', '2128', '2129',
  '2130', '2131', '2132', '2133', '2134', '2135', '2136', '2137', '2138', '2139',
  '2140', '2141', '2142', '2143', '2144', '2145', '2146', '2147', '2148', '2150',
  '2151', '2152', '2153', '2154', '2155', '2156', '2157', '2158', '2159', '2160',
  '2161', '2162', '2163', '2164', '2165', '2166', '2167', '2168', '2170', '2171',
  '2172', '2173', '2174', '2175', '2176', '2177', '2178', '2179', '2190', '2191',
  '2192', '2193', '2194', '2195', '2196', '2197', '2198', '2199', '2200', '2203',
  '2204', '2205', '2206', '2207', '2208', '2209', '2210', '2211', '2212', '2213',
  '2214', '2216', '2217', '2218', '2219', '2220', '2221', '2222', '2223', '2224',
  '2225', '2226', '2227', '2228', '2229', '2230', '2231', '2232', '2233', '2234',
  '2555', '2556', '2557', '2558', '2559', '2560', '2563', '2564', '2565', '2566',
  '2567', '2568', '2569', '2570', '2571', '2572', '2573', '2574', '2745', '2747',
  '2748', '2749', '2750', '2752', '2753', '2754', '2755', '2756', '2757', '2758',
  '2759', '2760', '2761', '2762', '2763', '2765', '2766', '2767', '2768', '2769',
  '2770', '2773', '2774', '2775', '2776', '2777', '2778', '2779', '2780', '2782',
  '2783', '2784', '2785', '2786', '2787', '2790',
])

/** Out-of-area validation message (UAT-36 spec §5.3, verbatim). */
export const SYDNEY_OUT_OF_AREA_MESSAGE =
  'Your postcode sits outside our delivery area, please contact us to make alternate arrangements.'

/** Normalise + test a postcode against the Greater Sydney gate. */
export function isGreaterSydneyPostcode(postcode: string | null | undefined): boolean {
  const cleaned = (postcode ?? '').trim().replace(/\s+/g, '')
  if (!/^\d{4}$/.test(cleaned)) return false
  return GREATER_SYDNEY_POSTCODES.has(cleaned)
}
